import { forwardRef, HTMLAttributes, useMemo, useRef, useState } from 'react'
import { useIsomorphicLayoutEffect, useKey } from 'react-use'
import gsap from 'gsap'
import Lenis from 'lenis'

import Form from '@/components/base/Form'
import Image from '@/components/base/Image'
import { ModalActions } from '@/components/base/Modal'
import SVGIcon from '@/components/base/SVGIcon'
import Typography from '@/components/base/Typography'
import { useScrollbar } from '@/contexts/lenis'
import { useStore } from '@/contexts/store'

import { cn } from '@/lib/utils'

export type NewsletterProps = HTMLAttributes<HTMLDivElement> & ModalActions

const Newsletter = forwardRef<HTMLDivElement, NewsletterProps>(
  ({ onClose, className, ...props }, ref) => {
    const { strings } = useStore()
    const [lenis, setLenis] = useState<Lenis>()
    const [track, thumb, needsScrollbar] = useScrollbar(lenis)
    const wrapper = useRef<HTMLDivElement>(null!)
    const content = useRef<HTMLDivElement>(null!)
    const form = useMemo(() => strings?.advisor_banner, [strings])

    useIsomorphicLayoutEffect(() => {
      const lenis = new Lenis({
        smoothWheel: true,
        wrapper: wrapper.current,
        content: content.current
      })

      const tick = (time: number) => {
        lenis.raf(time * 1000)
      }

      gsap.ticker.add(tick)

      setLenis(lenis)

      return () => {
        gsap.ticker.remove(tick)
        lenis.destroy()
        setLenis(undefined)
      }
    }, [])

    useKey('Escape', onClose)

    return (
      <div
        ref={ref}
        className={cn([
          'fixed left-0 top-0 z-modal h-full w-full bg-dark-blue/30',
          'flex p-gutter',
          className
        ])}
        {...props}
      >
        <div className="relative m-auto h-full w-full max-w-[64.5rem]">
          <button
            className={cn([
              'absolute right-gutter top-gutter z-10',
              'flex h-[4.4rem] w-[4.4rem] items-center justify-center rounded-full bg-dark-blue'
            ])}
            onClick={onClose}
          >
            <span className="sr-only">Chiudi</span>
            <SVGIcon
              symbol="close-icon"
              className="h-[1.4rem] w-[1.4rem] fill-white"
            />
          </button>

          <div
            ref={wrapper}
            className="h-full min-h-full w-full overflow-auto rounded-[3rem] bg-white scrollbar-hide"
          >
            <div ref={content} className="h-auto w-full">
              <div className="aspect-h-[276] aspect-w-[350] h-full w-full rounded-t-[3rem] bg-dark-blue/40 md:aspect-h-[300] md:aspect-w-[645]">
                <Image
                  source={form.img}
                  fill
                  fit="cover"
                  className="rounded-t-[3rem]"
                />
              </div>

              <div className="grid gap-y-gutter p-gutter">
                <div className="grid gap-y-[1.5rem]">
                  {form.texts.title ? (
                    <Typography
                      variant="h4"
                      component="h4"
                      className="text-dark-blue"
                    >
                      {form.texts.title}
                    </Typography>
                  ) : null}

                  {form.texts.text ? (
                    <Typography variant="body" className="text-dark-blue">
                      {form.texts.text}
                    </Typography>
                  ) : null}
                </div>

                <div>
                  <Form
                    form_id={form?.form_id}
                    fields={form?.fields}
                    submit={form?.submit}
                    theme="dark"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className={cn([
            'group absolute bottom-0 right-0 top-0 z-scrollbar hidden w-[1.2rem]',
            {
              visible: needsScrollbar,
              invisible: !needsScrollbar
            }
          ])}
        >
          <div ref={track} className="relative h-full w-full">
            <div
              ref={thumb}
              className={cn([
                'absolute left-0 top-0 w-full',
                'before:absolute before:bottom-[.2rem] before:left-[.4rem] before:top-[.2rem] before:w-[.6rem] before:rounded-full before:bg-dark-blue'
              ])}
            />
          </div>
        </div>
      </div>
    )
  }
)

export default Newsletter
